import {
    defineComponent,
    PropType,
    ref,
    watch,
} from "vue";
import { DefaultEntityClient } from "../core/api/EntityClient.js";
import type { LogChannel } from "../core/types.js";
import Store from "../store.js";

const ChannelCacheByGuild = new Map<string, Promise<Map<string, LogChannel>>>();

const ChannelIcons = new Map<number, string>([
    [ 0, "tag" ],
    [ 2, "settings_voice" ],
    [ 5, "campaign" ],
    [ 10, "tag" ],
    [ 11, "tag" ],
    [ 12, "tag" ],
    [ 13, "podcasts" ],
    [ 15, "forum" ],
    [ 16, "perm_media" ],
]);

function loadGuildChannels(guildId: string): Promise<Map<string, LogChannel>> {
    let cached = ChannelCacheByGuild.get(guildId);

    if (!cached) {
        cached = DefaultEntityClient.getChannels()
            .then((channels) => new Map(channels.map((channel) => [ channel.id, channel ])))
            .catch(() => new Map<string, LogChannel>());

        ChannelCacheByGuild.set(guildId, cached);
    }

    return cached;
}

function getChannelIcon(channel: LogChannel | null): string {
    if (typeof channel?.type !== "number")
        return "tag";

    return ChannelIcons.get(channel.type) ?? "tag";
}

function getFallbackChannel(id: string, fallbackLabel: string | null): LogChannel {
    return {
        id,
        name: fallbackLabel || `Salon ${id}`,
    };
}

export default defineComponent({
    props: {
        id: {
            type: String,
            required: true,
        },

        fallbackLabel: {
            type: String as PropType<string | null>,
            default: null,
        },
    },

    setup(props) {
        const channel = ref<LogChannel | null>(null);

        watch(
            () => [ Store.selectedGuild?.id, props.id ] as const,
            async ([ guildId, channelId ]) => {
                channel.value = null;

                if (!guildId || !channelId)
                    return;

                channel.value = (await loadGuildChannels(guildId)).get(channelId) ?? null;
            },
            { immediate: true },
        );

        return () => {
            const resolvedChannel = channel.value ?? getFallbackChannel(props.id, props.fallbackLabel);

            return (
                <router-link
                    to={`/channels/${props.id}`}
                    class="markdown-channel-mention">
                    <span class="material-symbols-outlined markdown-channel-mention__icon">
                        {getChannelIcon(resolvedChannel)}
                    </span>
                    <span>#{resolvedChannel.name}</span>
                </router-link>
            );
        };
    },
});
